/** @file Draws a GizmoDrawing (screen pixels) into overlay chunks in skeleton space; returns the chunks used. */
import type { GizmoHandle, GizmoPart } from '@pose2d/editor-geometry/types';
import { HANDLE_PX } from '@pose2d/editor-geometry/handlePx';
import type { Camera, RenderBuffers, TexturePages } from '@pose2d/geometry/types';
import { createOverlayChunk } from '@pose2d/overlay/createOverlayChunk';
import { createSink, type Sink } from '@pose2d/overlay/draw/createSink';
import { emitDisc } from '@pose2d/overlay/draw/emitDisc';
import { emitLine } from '@pose2d/overlay/draw/emitLine';
import { emitRing } from '@pose2d/overlay/draw/emitRing';
import { fillSegment } from '@pose2d/overlay/draw/fillSegment';
import { finishSink } from '@pose2d/overlay/draw/finishSink';
import { OVERLAY_STYLE } from '@pose2d/overlay/overlayStyle';
import type { FontAtlas } from '@pose2d/overlay/types';
import type { GizmoDrawing } from './viewportTypes';

const S = OVERLAY_STYLE;
const COLOR: Readonly<Record<GizmoPart, number>> = { free: S.selected, axisX: S.axisX, axisY: S.axisY, ring: S.selected, uniform: S.selected, scaleX: S.axisX, scaleY: S.axisY };
const seg = new Float64Array(4);
const allocate = (): RenderBuffers => createOverlayChunk({ vertices: 4096 });

/** One handle in its shape: disc, thick segment (2 px), or ring stroke (2 px). */
function emitHandle(sink: Sink, h: GizmoHandle, light: number): void {
  switch (h.shape) {
    case 'disc': emitDisc(sink, h.center[0], h.center[1], h.radius, light); return;
    case 'segment': emitLine(sink, fillSegment(seg, h.a[0], h.a[1], h.b[0], h.b[1]), 2, light); return;
    case 'ring': emitRing(sink, h.center[0], h.center[1], h.radius, 2, light); return;
  }
}

/** Lines first, handles (the dragged part white), then points; the overlay's white page, blend normal. */
export function drawGizmo(out: RenderBuffers[], drawing: GizmoDrawing, camera: Camera, textures: TexturePages, font: FontAtlas): number {
  const sink = createSink(out, { camera, textures, font, allocate });
  for (const [a, b] of drawing.lines) emitLine(sink, fillSegment(seg, a[0], a[1], b[0], b[1]), S.lineWidth, S.selected);
  for (const h of drawing.handles) emitHandle(sink, h, h.part === drawing.active ? S.joint : COLOR[h.part]);
  for (const p of drawing.points) emitDisc(sink, p.at[0], p.at[1], HANDLE_PX.point, p.color);
  return finishSink(sink);
}
